// src/components/support/forms/ExtendedDaycare.js
import React, { useEffect, useState } from "react";
import { ChevronLeft } from "lucide-react";
import { useRequestProcessor } from "../../../requestProcessor";
import SpinnerLoading from "../../common/SpinnerLoading";
import FootprintDatePicker from "../../common/FootprintDatePicker";
import moment from "moment";

import { useAtom } from "jotai";
import { activeChildDetail, appConstants, childList } from "../../../atomstore/store";
import { useNavigate } from "react-router-dom";

import { getSaturdayServiceStatus, submitExtendedDaycare } from "../../../queryfunctions/queryfunctions";
import CustomSelect from "../../common/CustomSelect";

const isSunday = (date) => moment(date).day() === 0;
const isSaturday = (date) => moment(date).day() === 6;
const isEligibleExtendedDaycareChild = (child) =>
    child?.pause_state !== 1 && child?.length?.includes("Half Day");

const sameChildId = (left, right) =>
    left != null && right != null && String(left) === String(right);

const ExtendedDaycare = () => {
    const navigate = useNavigate();
    const { useMutationWrapper, useQueryWrapper } = useRequestProcessor();
    const [children] = useAtom(childList);
    const [activeChild] = useAtom(activeChildDetail);
    const [constants] = useAtom(appConstants);

    const eligibleChildren = (children || []).filter(isEligibleExtendedDaycareChild);
    const [dropdownChildId, setDropdownChildId] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [error, setError] = useState("");

    const selectedChildData =
        (dropdownChildId != null
            ? (children || []).find((child) => sameChildId(child?.id, dropdownChildId))
            : (children || []).find((child) => sameChildId(child?.id, activeChild?.id)) ||
              activeChild) || null;
    const selectedCenterId = selectedChildData?.center_id;

    const saturdayServiceQuery = useQueryWrapper(
        ["saturdayServiceStatus", selectedCenterId, selectedChildData?.id],
        () => getSaturdayServiceStatus(selectedCenterId, selectedChildData),
        {
            enabled: !!selectedCenterId,
            refetchOnWindowFocus: false,
        }
    );
    const saturdayEnabled = saturdayServiceQuery?.data?.response?.saturday_enabled === true;

    const extendedDaycare = useMutationWrapper(["extendedDaycare"], () =>
        submitExtendedDaycare(
            startDate,
            endDate,
            selectedChildData
        )
    );

    useEffect(() => {
        const dateIsBlocked = (date) => {
            if (!date) return false;
            if (isSunday(date)) return true;
            return isSaturday(date) && !saturdayEnabled;
        };
        if (dateIsBlocked(startDate) || dateIsBlocked(endDate)) {
            setStartDate(null);
            setEndDate(null);
        }
    }, [saturdayEnabled, selectedChildData?.id, startDate, endDate]);

    const isBlockedWeekend = (date) => {
        if (isSunday(date)) return true;
        return isSaturday(date) && !saturdayEnabled;
    };

    const validate = () => {
        if (!startDate) {
            setError("Please select a start date");
            return false;
        } else if (!endDate) {
            setError("Please select an end date");
            return false;
        } else {
            const start = moment(startDate).startOf("day");
            const end = moment(endDate).startOf("day");
            const today = moment().startOf("day");

            if (start.isBefore(today) || end.isBefore(today)) {
                setError("Only current and future dates are allowed");
                return false;
            }
            if (end.isBefore(start)) {
                setError("End date cannot be before start date");
                return false;
            }
            const days = end.diff(start, "days");
            // Inclusive window: start date + 29 days = 30 total days
            if (days > 29) {
                setError("Maximum 30 days can be selected");
                return false;
            }
            if (isBlockedWeekend(start) || isBlockedWeekend(end)) {
                setError(
                    saturdayEnabled
                        ? "Sundays are not available for extended daycare"
                        : "Weekends are not available for extended daycare"
                );
                return false;
            }
        }
        setError("");
        return true;
    };

    const submitRequest = () => {
        if (validate()) extendedDaycare.mutate();
    };

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
            {(extendedDaycare.isLoading || saturdayServiceQuery.isLoading) && <SpinnerLoading />}

            {/* Header */}
            <div className="sticky top-0 bg-white border-b p-4 flex items-center space-x-3">
                <button onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2 className="text-lg font-bold text-gray-800 cursor-pointer" onClick={() => navigate(-1)}>Extended Daycare</h2>
            </div>

            <div className="p-4 space-y-4">

                {/* Child Selection */}
                {eligibleChildren.length > 1 && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Child
                        </label>
                        <CustomSelect
                            value={
                                selectedChildData?.id != null
                                    ? String(selectedChildData.id)
                                    : ""
                            }
                            onChange={(val) => setDropdownChildId(val)}
                            options={eligibleChildren.map((child) => ({
                                value: String(child?.id),
                                label: `${child?.name} ${child?.last_name}`,
                            }))}
                            placeholder="Select Child"
                        />
                    </div>
                )}

                {/* Info Box */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                    <p className="text-sm text-blue-800">
                        {(constants?.extendedDaycareInfoBanner ||
                            "Extend daycare services for {{name}}. Choose a start and end date below.")
                            .replace(
                                "{{name}}",
                                selectedChildData?.name?.split(" ")?.[0] || ""
                            )}
                    </p>
                </div>

                {/* Start Date */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date
                    </label>
                    <FootprintDatePicker
                        key={`edc-start-${selectedChildData?.id}-${saturdayEnabled}`}
                        minimumDate={moment().startOf("day")}
                        disableWeekends
                        enableSaturday={saturdayEnabled}
                        onChange={(date) => {
                            setStartDate(date);
                            // If end date is now outside the allowed window, clear it
                            if (endDate) {
                                const start = moment(date).startOf("day");
                                const end = moment(endDate).startOf("day");
                                const maxEnd = moment(date).add(29, "days").startOf("day");
                                if (end.isBefore(start) || end.isAfter(maxEnd)) {
                                    setEndDate(null);
                                }
                            }
                            setError("");
                        }}
                        inputProps={{
                            placeholder: "Select Date",
                        }}
                    />
                </div>

                {/* End Date */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date
                    </label>
                    <FootprintDatePicker
                        key={`edc-end-${selectedChildData?.id}-${saturdayEnabled}-${startDate || "none"}`}
                        disabled={!startDate}
                        minimumDate={startDate ? moment(startDate).startOf("day") : moment().startOf("day")}
                        maximumDate={startDate ? moment(startDate).add(29, "days").startOf("day") : undefined}
                        disableWeekends
                        enableSaturday={saturdayEnabled}
                        onChange={(date) => {
                            setEndDate(date);
                            setError("");
                        }}
                        inputProps={{
                            placeholder: "Select Date",
                            disabled: !startDate,
                        }}
                    />
                </div>

                {/* Error */}
                {error && <p className="text-red-500 text-sm">{error}</p>}

                {/* Submit */}
                <button
                    onClick={submitRequest}
                    disabled={extendedDaycare.isLoading || saturdayServiceQuery.isLoading}
                    className="w-full bg-cyan-500 text-white py-3 rounded-xl font-semibold hover:bg-cyan-600 transition disabled:opacity-60"
                >
                    Submit Request
                </button>
            </div>
        </div>
    );
};

export default ExtendedDaycare;
